import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { appEnv } from '@/app/env/appEnv';
import { useAuthStore } from '@/auth/handler/authStore';
import { composeEmail } from '@/support/data/supportRepository';
import { supportEmail } from '@/support/domain/supportContact';
import { PageChrome } from '@/ui/PageChrome';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { useThemeColors } from '@/settings/handler/settingsStore';

export function SupportScreen() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const user = useAuthStore((s) => s.user);
  const [mailFailed, setMailFailed] = useState(false);
  const [scrolledUnder, setScrolledUnder] = useState(false);

  const bodyStyle = {
    fontSize: 20,
    lineHeight: 1.2,
    color: colors.onSurface,
    opacity: 0.78,
  } as const;

  const compose = (): void => {
    const lines = ['', '', '—', `App: ${appEnv.version} (${appEnv.environment})`, 'Device: web'];
    if (!user.isAnonymous && user.username) lines.push(`Username: ${user.username}`);
    if (!user.isAnonymous && user.email) lines.push(`Account: ${user.email}`);
    const opened = composeEmail(t('supportMailSubject'), lines.join('\n'));
    setMailFailed(!opened);
  };

  return (
    <PageChrome title={t('supportTitle')} scrolledUnder={scrolledUnder}>
      <div
        className="flex-1 overflow-y-auto px-6 pb-28 pt-5"
        onScroll={(event) => setScrolledUnder(event.currentTarget.scrollTop > 0.5)}
      >
        <p style={bodyStyle}>{t('supportIntro')}</p>
        <h2 className="mt-[22px] leading-none" style={{ fontSize: 24, color: colors.onSurface }}>
          {t('supportIncludeTitle')}
        </h2>
        <p className="mt-3" style={bodyStyle}>
          {t('supportIncludeBody')}
        </p>
        <p className="mt-[22px] leading-none" style={{ fontSize: 22, color: colors.onSurface }}>
          {supportEmail}
        </p>
        <p className="mt-2.5" style={bodyStyle}>
          {t('supportReplyHint')}
        </p>
        <div className="mt-7">
          <PixelContainer
            radius={9}
            pixelScale={2}
            padding="16px 0"
            backgroundColor={colors.onSurface}
            borderColor={colors.onSurface}
            onClick={compose}
          >
            <span className="block text-center leading-none" style={{ fontSize: 22, color: colors.surface }}>
              {t('supportCta')}
            </span>
          </PixelContainer>
        </div>
        {mailFailed && (
          <p className="mt-4 text-center leading-tight" style={{ fontSize: 18, color: colors.onSurface }}>
            {t('supportMailFailed')}
          </p>
        )}
      </div>
    </PageChrome>
  );
}
